/**
 * Central language → direction / Intl-locale helpers. Replaces the ~100
 * scattered `startsWith('ar') ? 'ar-IQ' : 'en-US'` branches that left Kurdish
 * (ckb) users seeing English dates/numbers. Use these everywhere instead.
 */

/** Both Arabic and Kurdish Sorani are RTL (Arabic script). English is LTR. */
export function isRtlLang(lang: string): boolean {
	return lang.startsWith("ar") || lang.startsWith("ckb");
}

/**
 * BCP-47 locale for Intl date/number formatting. ckb maps to `ckb-IQ` (Iraqi
 * Kurdistan); ar → `ar-IQ`; everything else → `en-US`.
 */
export function localeFor(lang: string): string {
	if (lang.startsWith("ar")) return "ar-IQ";
	if (lang.startsWith("ckb")) return "ckb-IQ";
	return "en-US";
}

/**
 * DIGIT POLICY (P1-phone-i18n): clinical and financial numbers are ALWAYS
 * rendered with LATIN digits (0-9), never Eastern-Arabic (٠-٩), regardless of
 * UI language — mixing the two on one screen caused misreads. The `-u-nu-latn`
 * Unicode extension keeps the locale's own separators/order but forces Latin
 * numerals. Pair numeric output with the `tabular-nums` Tailwind class so
 * columns align.
 */
export function latnLocaleFor(lang: string): string {
	return `${localeFor(lang)}-u-nu-latn`;
}

/**
 * DATE POLICY (RTL-dates fix): dates and times are ALWAYS rendered in a fixed
 * English, Latin-digit form — never localized to Arabic/Kurdish — regardless of
 * UI language. Localized dates produced Arabic month/weekday NAMES (يونيو،
 * الخميس) that the bidi algorithm visually reversed inside RTL layouts
 * ("خميس/رهش/موي"), and hard-coded `ar-IQ` callers stayed Arabic even in the
 * English UI. The product rule: full dates show ISO `YYYY-MM-DD`; compact
 * calendar/chart labels (passed via `opts`) use English names through this
 * locale. Always pair the visible output with <DateText> (or `dir="ltr"`) so
 * the LTR string can't be mirrored in an RTL container.
 */
export const DATE_LOCALE = "en-GB-u-nu-latn";

function pad2(n: number): string {
	return String(n).padStart(2, "0");
}

/** Local-time ISO date — `YYYY-MM-DD`, Latin digits, never localized. */
function isoDate(d: Date): string {
	return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
	if (value == null || value === "") return null;
	const d = value instanceof Date ? value : new Date(value);
	return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Null-safe localized number — THE central formatter for clinical/financial
 * values (counts, money, lab values). Always Latin digits (see DIGIT POLICY).
 * Returns '—' for null/undefined/NaN instead of throwing.
 */
export function formatNumber(
	value: number | string | null | undefined,
	lang: string,
	opts: Intl.NumberFormatOptions = {},
): string {
	if (value == null || value === "") return "—";
	const n = typeof value === "number" ? value : Number(value);
	if (Number.isNaN(n)) return "—";
	try {
		return n.toLocaleString(latnLocaleFor(lang), opts);
	} catch {
		return String(n);
	}
}

/**
 * Null-safe date string per the DATE POLICY above: ISO `YYYY-MM-DD` by default,
 * or an English compact label when `opts` is given (e.g. `{ weekday: "short" }`
 * → "Sun", `{ month: "short", day: "numeric" }` → "19 Jun"). Never produces
 * Arabic names or Eastern-Arabic digits. Returns '—' for null/invalid. The
 * `lang` argument is accepted for call-site compatibility but intentionally
 * ignored — date output is language-independent.
 */
export function formatDate(
	value: string | number | Date | null | undefined,
	_lang?: string,
	opts?: Intl.DateTimeFormatOptions,
): string {
	const d = toDate(value);
	if (!d) return "—";
	if (!opts) return isoDate(d);
	try {
		return d.toLocaleDateString(DATE_LOCALE, opts);
	} catch {
		return isoDate(d);
	}
}

/** Null-safe time `HH:MM` (24h, Latin digits, never localized). */
export function formatTime(
	value: string | number | Date | null | undefined,
): string {
	const d = toDate(value);
	if (!d) return "—";
	return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Null-safe date+time — ISO date + 24h `HH:MM` (e.g. `2026-06-19 14:30`). */
export function formatDateTime(
	value: string | number | Date | null | undefined,
	_lang?: string,
): string {
	const d = toDate(value);
	if (!d) return "—";
	return `${isoDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
